import {
    COOKIE_VISITOR_ID,
    OC_SHOW_WELCOME_MODALS_COOKIE_NAME,
} from '@marketplace/config/cookie.js'
import { DEFAULT_LOCALE } from '@marketplace/config/i18n.js'
import { OC_SIGNUP_RETURN_URL } from '@marketplace/config/localStorage.js'
import Cookies from 'js-cookie'

/**
 * Get the ISO code locale from a url.
 *
 * @param {string} href
 * @returns {string}
 */
export function getLocaleFromUrl(href) {
    const url = new URL(href)

    const capturingRegex = /\/(?<locale>nl|de|fr|pl|da|it|es)(\/|$)/i
    const match = url.pathname.match(capturingRegex)

    if (match?.groups.locale) {
        return match.groups.locale
    }

    return DEFAULT_LOCALE
}

/**
 * Get the previous URL from either Vue router or the HTTP referrer.
 *
 * @param {object} from
 * @returns {string}
 */
export function getVueReferrer(from) {
    if (!from?.name) {
        return document.referrer
    }

    return `${window.location.protocol}//${window.location.hostname}${from.fullPath}`
}

/**
 * Check if valid url.
 *
 * @param {string} string
 * @returns {boolean}
 */
export function isValidUrl(string) {
    try {
        new URL(string)
    } catch (_) {
        return false
    }

    return true
}

/**
 * Check if the url contains a feature toggle.
 *
 * Note: this function is not reactive so it persists when using Vue router until a refresh.
 *
 * @param {string} featureKey
 * @returns {boolean}
 */
export function hasFeature(featureKey) {
    return new URLSearchParams(window.location.search).has(featureKey)
}

/**
 * Localize the a url.
 *
 * Note: this function is not reactive so it persists when using Vue router until a refresh.
 *
 * @param {string} featureKey
 * @returns {boolean}
 */
export function getFeatureValue(featureKey) {
    return new URLSearchParams(window.location.search).get(featureKey)
}

/**
 * Get the correct localized url or strip the locale for the default language.
 *
 * @param {string}  path
 * @param {string}  locale
 * @param {boolean} isHubSpot
 * @returns {string}
 */
export function localeUrl(path, locale = null, isHubSpot = false) {
    const currentLocale = window.Store?.locale?.toLowerCase() || DEFAULT_LOCALE

    // Remove locale from path
    const localeMatch = path.match(
        new RegExp('^/' + currentLocale + '(/?|$)', 'i')
    )

    if (currentLocale && localeMatch) {
        path = path.substr(localeMatch[0].length)
    }

    let strippedPath = path.replace(/^\/|\/$/g, '')
    const newLocale = (locale || currentLocale).toLowerCase()

    if (strippedPath.includes('{locale}')) {
        return path
            .replace('{locale}', newLocale === 'en' ? '' : newLocale)
            .replace('//', '/')
    }

    if (newLocale !== 'en') {
        strippedPath = strippedPath ? '/' + strippedPath : ''
        if (isHubSpot) {
            return strippedPath + '/' + newLocale
        }
        return '/' + newLocale + strippedPath
    }

    return '/' + strippedPath
}

/**
 * Get the correct localized external url.
 * If the URL already contains a locale, it will be used as-is.
 * If no locale is present, the current locale from the URL will be used.
 * For unsupported locales (like 'fr'), it will fall back to 'en'.
 * For 'en' locale, no locale will be added to the URL.
 *
 * @param {string} url
 * @returns {string}
 */
export function externalFramerTrackedLocaleUrl(url) {
    // Supported locales for external websites
    const SUPPORTED_EXTERNAL_LOCALES = ['en', 'nl', 'de']

    // Remove trailing slash if present
    url = url.replace(/\/$/, '')

    // Split URL into domain and path
    const urlObj = new URL(url)
    const path = urlObj.pathname.replace(/^\/+/, '')

    // Get visitor ID from cookie
    const visitorId = Cookies.get(COOKIE_VISITOR_ID)

    // Create search params from existing URL search params
    const searchParams = new URLSearchParams(urlObj.search)

    // Add visitor ID if it exists
    if (visitorId) {
        searchParams.set('visitorId', visitorId)
    }

    // Check if URL already contains a locale
    const urlLocale = getLocaleFromPath(url)
    if (urlLocale !== 'en') {
        return `${urlObj.origin}/${path}${
            searchParams.toString() ? '?' + searchParams.toString() : ''
        }`
    }

    // Get current locale from Store and handle fallback
    let currentLocale = window.Store?.locale?.toLowerCase() || DEFAULT_LOCALE
    if (!SUPPORTED_EXTERNAL_LOCALES.includes(currentLocale)) {
        currentLocale = 'en'
    }

    // For 'en' locale, return the URL as-is
    if (currentLocale === 'en') {
        return `${urlObj.origin}/${path}${
            searchParams.toString() ? '?' + searchParams.toString() : ''
        }`
    }

    // Reconstruct URL with locale and search params
    return `${urlObj.origin}/${currentLocale}${path ? '/' + path : ''}${
        searchParams.toString() ? '?' + searchParams.toString() : ''
    }`
}

/**
 * Get the locale from a path
 *
 * @param {string} path
 * @returns {string}
 */
export function getLocaleFromPath(path) {
    const capturingRegex = /\/(?<locale>nl|de|fr|pl|da|it|es)(\/|$)/i
    const match = path.match(capturingRegex)

    if (match?.groups.locale) {
        return match.groups.locale
    }

    return 'en'
}

/**
 * @param pathSuffix
 * @param queryParams
 */
export function navigateToDropshipping(pathSuffix = '', queryParams = '') {
    const baseURL = 'https://www.orderchamp.com/dropshipping'
    const locale =
        (typeof window !== 'undefined' &&
            window.Store &&
            window.Store.locale) ||
        null
    let finalURL = baseURL

    // Handle locale mapping
    const localeMapping = {
        nl: 'nl-nl',
        de: 'de-de',
        fr: 'fr-fr',
        en: 'en-gb',
    }

    // Apply mapped locale if pathSuffix includes '/brands'
    if (locale && localeMapping[locale] && pathSuffix.includes('/brands')) {
        finalURL = `${baseURL}/${localeMapping[locale]}`
    } else if (['nl', 'de', 'fr'].includes(locale)) {
        // Otherwise, use the original locale
        finalURL = `${baseURL}/${locale}`
    }

    // Add pathSuffix if applicable
    if (pathSuffix) {
        finalURL = `${finalURL}${pathSuffix}`
    }

    // Add queryParams if applicable
    if (queryParams) {
        finalURL = `${finalURL}?${queryParams}`
    }

    // Return the generated URL
    return finalURL
}

/**
 *
 * @param root0
 * @param root0.signupIntent
 * @param root0.isTicaAccount
 * @param root0.lastUrlBeforeSignup
 * @param root0.hasOneBrandFeature
 * @param root0.slug
 */
export function performRedirection({
    signupIntent,
    isTicaAccount,
    hasOneBrandFeature,
    slug,
}) {
    Cookies.set(OC_SHOW_WELCOME_MODALS_COOKIE_NAME, 1)
    const lastUrlBeforeSignup =
        localStorage.getItem(OC_SIGNUP_RETURN_URL) || localeUrl('/')

    if (hasOneBrandFeature && slug) {
        window.location.href = localeUrl(`ds/store/${slug}`)
        return
    }

    if (signupIntent === 'dropshipping') {
        window.location.href = localeUrl('ds/signup')
        return
    }

    if (lastUrlBeforeSignup.includes('tica')) {
        const targetPath = isTicaAccount ? 'tica/connected' : '/'
        window.location.href = localeUrl(targetPath)
        return
    }

    if (lastUrlBeforeSignup.includes('retailers')) {
        window.location.href = localeUrl('/')
        return
    }

    window.location.href = lastUrlBeforeSignup
    localStorage.removeItem(OC_SIGNUP_RETURN_URL)
}

/**
 * Replace all orderportal.com and orderchamp.cloud links in HTML text with localized versions using externalFramerTrackedLocaleUrl
 *
 * @param {string} htmlText - The HTML text containing orderportal.com and orderchamp.cloud links
 * @returns {string} - The HTML text with replaced links
 */
export function replaceFramerLinks(htmlText) {
    if (!htmlText) return htmlText

    // Use regex to find and replace href attributes containing orderportal.com or orderchamp.cloud
    return htmlText.replace(
        /href="([^"]*(?:orderportal\.com|orderchamp\.cloud)[^"]*)"/g,
        (match, url) => `href="${externalFramerTrackedLocaleUrl(url)}"`
    )
}
